import { UpdateTicketPayload, useRestApiProvider } from "@jugl-web/rest-api";
import { TicketDetailsModel } from "@jugl-web/rest-api/tickets/models";
import { TicketListItemModel } from "@jugl-web/rest-api/tickets/models/TicketListItem";
import { WithOptimistic } from "@jugl-web/utils";
import { Dispatch, SetStateAction, useCallback, useRef } from "react";

interface UseUpdateTicketOptimisticallyOptions {
  entityId: string;
  setTickets: Dispatch<SetStateAction<WithOptimistic<TicketListItemModel>[]>>;
}

/**
 * Hook that applies optimistic updates to the local ticket list.
 */
export const useUpdateTicketOptimistically = ({
  entityId,
  setTickets,
}: UseUpdateTicketOptimisticallyOptions) => {
  const { ticketsApi } = useRestApiProvider();
  const [updateTicketMutation] = ticketsApi.useUpdateTicketMutation();

  /** Snapshots of tickets before the optimistic update was applied. */
  const snapshotsRef = useRef<Map<string, TicketListItemModel>>(new Map());

  const applyOptimisticUpdate = useCallback(
    (
      ticketId: string,
      updateFn: (ticket: TicketListItemModel) => Partial<TicketListItemModel>
    ) => {
      setTickets((prev) =>
        prev.map((ticket) => {
          if (ticket.id !== ticketId) {
            return ticket;
          }

          // Save a snapshot before the first in-flight update for this ticket.
          if (!snapshotsRef.current.has(ticketId)) {
            snapshotsRef.current.set(ticketId, ticket);
          }

          return { ...ticket, ...updateFn(ticket), _optimistic: true };
        })
      );
    },
    [setTickets]
  );

  const confirmOptimisticUpdate = useCallback(
    (ticketId: string, updates: Partial<TicketListItemModel>) => {
      setTickets((prev) =>
        prev.map((ticket) =>
          ticket.id === ticketId
            ? { ...ticket, ...updates, _optimistic: false }
            : ticket
        )
      );
      snapshotsRef.current.delete(ticketId);
    },
    [setTickets]
  );

  const rollbackOptimisticUpdate = useCallback(
    (ticketId: string) => {
      const snapshot = snapshotsRef.current.get(ticketId);

      if (!snapshot) {
        return;
      }

      setTickets((prev) =>
        prev.map((ticket) => (ticket.id === ticketId ? snapshot : ticket))
      );

      snapshotsRef.current.delete(ticketId);
    },
    [setTickets]
  );

  /**
   * Optimistically apply `data` to the local list, call the API, then either
   * confirm (from the server response) or roll back on failure.
   */
  const updateTicket = useCallback(
    async (
      ticketId: string,
      data: UpdateTicketPayload
    ): Promise<TicketDetailsModel | undefined> => {
      applyOptimisticUpdate(ticketId, () => ({
        ...(data.status !== undefined && { status: data.status }),
        ...(data.title !== undefined && { title: data.title }),
        ...(data.priority !== undefined && { priority: data.priority }),
        ...(data.due_at !== undefined && { due_at: data.due_at }),
      }));

      const response = await updateTicketMutation({ entityId, ticketId, data });

      if ("data" in response) {
        const updatedTicket = response.data;
        confirmOptimisticUpdate(ticketId, updatedTicket);
        return updatedTicket;
      }

      rollbackOptimisticUpdate(ticketId);
      return undefined;
    },
    [
      applyOptimisticUpdate,
      confirmOptimisticUpdate,
      rollbackOptimisticUpdate,
      updateTicketMutation,
      entityId,
    ]
  );

  return { updateTicket };
};
